import React, { useContext, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import {
    PortalPageShell,
    PortalCaption,
    PortalKicker,
    PortalPanelHeader,
    PortalSectionTitle,
    PortalSurface
} from "src/components";
import { UserContext } from "src/scenes/App/UserContext";
import { PageLoadingContainer } from "./components/PageLoadingComponent";
import BodyAndMindHeader from "./components/ContentHeader";
import ManageCategory from "./components/Categories/ManageCategories";
import { ContentTable } from "./components/Content/ContentTable";
import HeaderCards from "./components/HeaderCards";
import Categories from "src/img/bodyAndMind/categories.svg";
import SubCategories from "src/img/bodyAndMind/subcategories.svg";
import Content from "src/img/bodyAndMind/content.svg";
import ManageSubCategories from "./components/SubCategories/ManageSubCategories";
import AddContent from "./components/Content/AddContent";
import { CardHolder } from "./components/style";
import EditContent from "./components/Content/EditContent";
import { Toast } from "src/components";
import { UserKeys } from "src/constants/userDetails";
import { getCategories, getCompanyBodyAndMindSettings, getContents } from "./api";
import { BodyAndMindContext } from "./BodyAndMindContext";
import { getSubcategories } from "./api/subCategoryApi";
import ShowNewsFeed from "./components/Content/ShowNewsFeed";
import ChangeNameDialog from "./components/Content/ChangeNameDialog";
import { fetchCoverByUuid, fetchCategoryByUuid } from "./api/videoStatusApi";
export const DefaultBodyAndMindFeatureName = "Body & Mind";

function BodyAndMindList({ user }) {

    const { appFns } = useContext(UserContext);

    const [featureName, setFeatureName] = useState(user[UserKeys.CUSTOM_NAME_BODY_AND_MIND] || DefaultBodyAndMindFeatureName);

    const [contentsList, setContentsList] = useState([]);
    const [contentListIsFetching, setContentListIsFetching] = useState(false);
    const [contentListHasFetched, setContentListHasFetched] = useState(false);
    const [categoriesList, setCategoriesList] = useState([]);
    const [subcategoriesList, setSubcategoriesList] = useState([]);
    const [subcategoriesListIsFetching, setSubcategoriesListIsFetching] = useState(true);
    const [companyCategoriesList, setCompanyCategoriesList] = useState([]);

    const [showAddContent, setShowAddContent] = useState(false);
    const [showEditContent, setShowEditContent] = useState(false);
    const [selectedContent, setSelectedContent] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState("");
    const [recentFromMeApp, setRecentFromMeApp] = useState(null);
    const [showNewsFeedOption, setShowNewsFeedOption] = useState(false);
    const [showChangeNameDialog, setShowChangeNameDialog] = useState(false);

    const [showManageCategory, setShowManageCategory] = useState(false);
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [showEditCategory, setShowEditCategory] = useState(false);

    const [showManageSubcategory, setShowManageSubcategory] = useState(false);
    const [showAddSubcategory, setShowAddSubcategory] = useState(false);
    const [showEditSubcategory, setShowEditSubcategory] = useState(false);

    const toggleAddContentModal = () => setShowAddContent(!showAddContent);
    const toggleEditContentModal = () => setShowEditContent(!showEditContent);
    const toggleShowNewsFeedOption = () => setShowNewsFeedOption(!showNewsFeedOption);

    const toggleManageCategory = () => setShowManageCategory(!showManageCategory);
    const toggleAddCategory = () => setShowAddCategory(!showAddCategory);
    const toggleEditCategory = () => setShowEditCategory(!showEditCategory);

    const toggleManageSubcategory = () => setShowManageSubcategory(!showManageSubcategory);
    const toggleAddSubcategory = () => setShowAddSubcategory(!showAddSubcategory);
    const toggleEditSubcategory = () => setShowEditSubcategory(!showEditSubcategory);

    const tableChangeCategory = event => {
        setSelectedCategory(event.target.value);
    };

    const getAllCategories = async () => {
        try {
            const response = await getCategories();
            if (response) setCategoriesList(response.categories);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error fetching list of categories");
        }
    };

    useEffect(() => {
        getAllCategories();
    }, [
        showManageSubcategory,
        showAddCategory,
        showEditCategory,
        showManageCategory,
        showManageSubcategory
    ]);

    const getAllSubcategories = async () => {
        setSubcategoriesListIsFetching(true);
        try {
            const response = await getSubcategories();
            if (response) setSubcategoriesList(response.categories);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error fetching list of categories");
        } finally {
            setSubcategoriesListIsFetching(false);
        }
    };

    useEffect(() => {
        getAllSubcategories();
    }, [
        showManageCategory,
        showManageSubcategory,
        showAddSubcategory,
        showEditSubcategory,
        showEditCategory
    ]);

    const getAllContents = async () => {
        setContentListIsFetching(true);
        try {
            const response = await getContents();
            if (response) setContentsList(response.contents);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error fetching list of categories");
        } finally {
            setContentListIsFetching(false);
            setContentListHasFetched(true);
        }
    };

    useEffect(() => {
        getAllContents();
    }, [
        showManageCategory,
        showManageSubcategory,
        showAddSubcategory,
        showEditSubcategory,
        showAddContent,
        showEditContent,
        showEditCategory
    ]);

    const getBodyAndMindSettings = async () => {
        try {
            const response = await getCompanyBodyAndMindSettings();
            setRecentFromMeApp(response.recentFromMeApp);
        } catch (error) {
            console.log("Error: ", error);
            Toast.error("Error fetching Body & Mind settings");
        }
    };

    useEffect(() => {
        getBodyAndMindSettings();
    }, []);

    // Create a new list of categories for company-specific entries only
    useEffect(() => {
        const newCategoriesList = categoriesList.filter(item => item.companyUuid !== null);
        setCompanyCategoriesList(newCategoriesList);
    }, [categoriesList]);

    const propagateFeatureName = newName => {
        setFeatureName(newName);
        appFns.updateUserContextProperties({
            [UserKeys.CUSTOM_NAME_BODY_AND_MIND]: newName
        });
    };

    // Polling for pending video after save with cropping
    useEffect(() => {
        let isCancelled = false;
        const pollVideos = async (videos) => {

            const pollTasks = videos.map(video =>
                new Promise((resolve) => {
                    (async () => {
                        let synced = false;

                        while (!synced && !isCancelled) {
                            try {
                                const res = await fetchCoverByUuid(video.uuid);
                                if (res?.pendingMedia === false) {
                                    synced = true;
                                } else {
                                    await new Promise(r => setTimeout(r, 5000));
                                }
                            } catch (err) {
                                console.error("Error polling video:", err);
                                synced = true;
                            }
                        }

                        if (!synced) {
                            console.warn(`Polling for video ${video.uuid} error.`);
                        }

                        resolve();
                    })();
                })
            );

            await Promise.allSettled(pollTasks);

            if (!isCancelled) {
                await getAllContents();
            }
        };

        // Poll categories video until the 'pendingMedia' is false
        const pollCategories = async (categories) => {

            const pollTasks = categories.map(category =>
                new Promise((resolve) => {
                    (async () => {
                        let synced = false;

                        while (!synced && !isCancelled) {
                            try {
                                const res = await fetchCategoryByUuid(category.uuid);
                                if (res?.pendingMedia === false) {
                                    synced = true;
                                } else {
                                    await new Promise(r => setTimeout(r, 5000));
                                }
                            } catch (err) {
                                console.error("Error polling category:", err);
                                synced = true;
                            }
                        }

                        if (!synced) {
                            console.warn(`Polling for category ${category.uuid} error.`);
                        }

                        resolve();
                    })();
                })
            );

            await Promise.allSettled(pollTasks);

            if (!isCancelled) {
                await getAllCategories();
            }
        };

        const pendingVideos = contentsList
            .filter(c => c.video?.pendingMedia === true)
            .map(c => c.video);

        const pendingCategories = categoriesList.filter(c =>
            c.pendingMedia === true &&
        c.headerVideoUrl?.toLowerCase().endsWith(".mp4")
        );

        if (pendingVideos.length > 0) {
            pollVideos(pendingVideos);
        }

        if (pendingCategories.length > 0) {
            pollCategories(pendingCategories);
        }

        return () => {
            isCancelled = true;
        };
    }, [contentsList, categoriesList]);

    return (
        <BodyAndMindContext.Provider
            value={{
                featureName,
                setShowChangeNameDialog,

                categoriesList,
                contentsList,
                contentListHasFetched,
                subcategoriesList,
                subcategoriesListIsFetching,
                showAddCategory,
                recentFromMeApp,
                showNewsFeedOption,
                showChangeNameDialog,
                setRecentFromMeApp,

                toggleManageCategory,
                toggleManageSubcategory,
                toggleShowNewsFeedOption,

                toggleAddCategory,
                showEditCategory,
                toggleEditCategory,

                showAddSubcategory,
                toggleAddSubcategory,
                showEditSubcategory,
                toggleEditSubcategory,
            }}
        >
            <PortalPageShell>
                <BodyAndMindHeader
                    includeChangeNameButton={true}
                    title={featureName}
                    description={`Create and organise content your way. With the ${featureName} feature, businesses can build custom categories and sub-categories to suit any purpose—wellbeing, professional development, internal training, or company resources. Upload your own unique content and deliver it directly to your users in the app, all in one organised space.`}
                    descriptionStyle={{
                        size: "14px"
                    }}
                />
                <PortalSurface padding="1rem" style={{ maxWidth: "98.625rem", marginBottom: "1rem" }}>
                    <PortalPanelHeader withDivider>
                        <div>
                            <PortalKicker>Library Structure</PortalKicker>
                            <PortalSectionTitle>{featureName} Overview</PortalSectionTitle>
                            <PortalCaption>
                                Manage categories, sub-categories, and the content available to users from a dedicated workspace.
                            </PortalCaption>
                        </div>
                    </PortalPanelHeader>
                    <CardHolder>
                        <HeaderCards
                            icon={Categories}
                            title={"Categories"}
                            quantity={categoriesList?.length ?? 0}
                            toggleFunction={toggleManageCategory}
                            width="257px"
                        />
                        <HeaderCards
                            icon={SubCategories}
                            title={"Sub-Categories, Topics & Sub-Topics"}
                            quantity={subcategoriesList?.length ?? 0}
                            toggleFunction={toggleManageSubcategory}
                            width="300px"
                        />
                        <HeaderCards
                            icon={Content}
                            title={"Content Available"}
                            quantity={contentsList?.length ?? 0}
                            width="199px"
                            toggleFunction={toggleShowNewsFeedOption}
                        />
                    </CardHolder>
                </PortalSurface>
                {/* Table */}
                <PageLoadingContainer isFetching={contentListIsFetching}>
                    <ContentTable
                        data={contentsList || []}
                        selectedCategory={selectedCategory}
                        tableChangeCategory={tableChangeCategory}
                        categoriesList={categoriesList}
                        subcategoriesList={subcategoriesList}
                        companyCategoriesList={companyCategoriesList}
                        manageContent={{ toggleAddContentModal, toggleEditContentModal, setSelectedContent }}
                    />
                </PageLoadingContainer>

                {/* Modal */}
                {showManageSubcategory && (
                    <ManageSubCategories />
                )}

                {showAddContent && (
                    <AddContent
                        toggleAddContentModal={toggleAddContentModal}
                        companyCategoriesList={companyCategoriesList}
                    />
                )}

                {showEditContent && (
                    <EditContent
                        toggleEditContentModal={toggleEditContentModal}
                        selectedContent={selectedContent}
                        companyCategoriesList={companyCategoriesList}
                    />
                )}

                {showManageCategory && (
                    <ManageCategory toggleManageCategory={toggleManageCategory} toggleAddCategory={toggleAddCategory} />
                )}

                {showNewsFeedOption && (
                    <ShowNewsFeed />
                )}

                {showChangeNameDialog && (
                    <ChangeNameDialog
                        hideShowNameChangeDialog={() => setShowChangeNameDialog(false)}
                        currentFeatureName={featureName}
                        setCustomBodyAndMindName={propagateFeatureName}
                    />
                )}
            </PortalPageShell>
        </BodyAndMindContext.Provider>
    );
}

export default withRouter(BodyAndMindList);
